import { FormEvent, ReactNode, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { IoIosInformationCircle } from "react-icons/io";

import Container from "components/__reusable/Container";
import ClassDropdown from "components/__reusable/ClassDropdown";
import TextInput from "components/__reusable/TextInput";
import DateInput from "components/__reusable/DateInput";
import MonthPickerInput from "components/__reusable/MonthInput";
import TrainingPictureInput from "./TrainingPictureInput";
import SavedTrainingPictures from "./SavedTraningPictures";
import Checkbox from "components/__reusable/Checkbox";
import FormActionBar from "components/__reusable/FormActionBar";
import useAppSelector from "hooks/useAppSelector";
import type { StudentFormData } from "types/Student";
import type { UpdatedTrainingPicture } from "types/TrainingPicture";
import { calculateGraduateDate } from "utilities/GraduateDateCalculator";
import { toMonthEnd, toMonthStart } from "utilities/MonthBoundary";

const MAX_DATE = new Date();
MAX_DATE.setHours(0, 0, 0, 0);

interface FormProps {
  isUpdate: boolean;
  student: StudentFormData;
  actionLeftContent?: ReactNode;
  onSubmit: (
    data: StudentFormData,
    pictures: File[],
    updatedPicture?: UpdatedTrainingPicture[],
  ) => Promise<void>;
}

const Form: React.FC<FormProps> = ({
  isUpdate,
  student,
  actionLeftContent,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const [classId, setClassId] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastNameKana, setLastNameKana] = useState("");
  const [firstNameKana, setFirstNameKana] = useState("");
  const [birthday, setBirthday] = useState(new Date());
  const [graduateDate, setGraduateDate] = useState(new Date());
  const [isSetToNotPublish, setIsSetToNotPublish] = useState(false);
  const [trainingPictures, setTrainingPictures] = useState<File[]>([]);
  const [updatedTrainingPictures, setUpdatedTrainingPictures] = useState<
    UpdatedTrainingPicture[]
  >([]);
  const { loading } = useAppSelector((state) => state.student);
  const [showGraduateTooltip, setShowGraduateTooltip] = useState(false);
  const [showReferenceTooltip, setShowReferenceTooltip] = useState(false);
  const [referenceTooltipPosition, setReferenceTooltipPosition] = useState({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (!isUpdate) {
      setClassId(student.class_id);
    } else {
      setFirstName(student.first_name);
      setLastName(student.last_name);
      setFirstNameKana(student.first_name_kata);
      setLastNameKana(student.last_name_kata);
      setBirthday(student.birthday ? new Date(student.birthday) : new Date());
      setGraduateDate(
        student.graduation_date
          ? toMonthStart(new Date(student.graduation_date))
          : toMonthStart(new Date()),
      );
      setClassId(student.class_id);
      setIsSetToNotPublish(student.is_ng);
    }
  }, [isUpdate, student]);

  useEffect(() => {
    if (!isUpdate) {
      const graduateDate = calculateGraduateDate(birthday);
      setGraduateDate(graduateDate);
    }
  }, [birthday, isUpdate]);

  const renderInput = (label: ReactNode, inputElement: ReactNode) => (
    <tr>
      <td className="align-top py-3 pr-6 w-1/4">
        <h3 className="font-medium text-left">{label}</h3>
      </td>
      <td className="align-top py-3 relative">{inputElement}</td>
    </tr>
  );

  const updateReferenceTooltipPosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    setReferenceTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    });
  };

  const [isDisabled, setIsDisabled] = useState(true);
  useEffect(() => {
    setIsDisabled(
      !classId ||
        !firstName ||
        !lastName ||
        !firstNameKana ||
        !lastNameKana ||
        birthday.toDateString() === new Date().toDateString(),
    );
  }, [classId, firstName, lastName, firstNameKana, lastNameKana, birthday]);

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    const formattedBirthday = format(birthday, "yyyy-MM-dd");
    const formattedGraduateDate = format(
      toMonthEnd(graduateDate),
      "yyyy-MM-dd",
    );
    const formData: StudentFormData = {
      first_name: firstName,
      last_name: lastName,
      first_name_kata: firstNameKana,
      last_name_kata: lastNameKana,
      birthday: formattedBirthday,
      graduation_date: formattedGraduateDate,
      class_id: classId,
      is_ng: isSetToNotPublish,
    };
    await onSubmit(formData, trainingPictures, updatedTrainingPictures);
    if (!isUpdate) {
      setFirstName("");
      setLastName("");
      setFirstNameKana("");
      setLastNameKana("");
      setClassId("");
      setBirthday(new Date());
      setTrainingPictures([]);
    }
  };

  return (
    <div className="space-y-3">
      <Container>
        <form className="w-full lg:w-3/4" onSubmit={submitForm}>
          <table className="w-full table-fixed">
            <tbody>
              {renderInput(
                `${t("_student_form._class_name")}(*)`,
                <ClassDropdown
                  currentClassId={classId}
                  onChange={(value) => setClassId(value.toString())}
                />,
              )}
              {renderInput(
                `${t("_student_form._student_name")}(*)`,
                <div className="w-full flex space-x-3">
                  <div className="w-1/2">
                    <TextInput
                      name={"last_name"}
                      placeholder={t("_student_form._last_name")}
                      value={lastName}
                      onChange={(value) => setLastName(value)}
                    />
                  </div>
                  <div className="w-1/2">
                    <TextInput
                      name={"first_name"}
                      placeholder={t("_student_form._first_name")}
                      value={firstName}
                      onChange={(value) => setFirstName(value)}
                    />
                  </div>
                </div>,
              )}
              {renderInput(
                "",
                <div className="w-full space-y-3">
                  <div className="w-full flex space-x-3">
                    <div className="w-1/2">
                      <TextInput
                        name={"last_name_kata"}
                        placeholder={t("_student_form._last_name_kana")}
                        value={lastNameKana}
                        onChange={(value) => setLastNameKana(value)}
                      />
                    </div>
                    <div className="w-1/2">
                      <TextInput
                        name={"first_name_kata"}
                        placeholder={t("_student_form._first_name_kana")}
                        value={firstNameKana}
                        onChange={(value) => setFirstNameKana(value)}
                      />
                    </div>
                  </div>
                </div>,
              )}
              {renderInput(
                `${t("_student_form._birthday")}(*)`,
                <div className="w-1/2">
                  <div className="relative">
                    <DateInput
                      name={"birthday"}
                      currentDate={birthday}
                      maxDate={MAX_DATE}
                      onChange={(value) => setBirthday(value)}
                    />
                  </div>
                </div>,
              )}
              {renderInput(
                <div className="flex items-center space-x-1">
                  <span>{t("_student_form._graduate_date")}</span>
                  <div
                    className="relative inline-flex"
                    tabIndex={0}
                    onMouseEnter={() => setShowGraduateTooltip(true)}
                    onMouseLeave={() => setShowGraduateTooltip(false)}
                    onFocus={() => setShowGraduateTooltip(true)}
                    onBlur={() => setShowGraduateTooltip(false)}
                  >
                    <IoIosInformationCircle
                      size={24}
                      className="rounded-full p-0.5 cursor-pointer text-gray-600"
                    />
                    {showGraduateTooltip && (
                      <div className="absolute left-1/2 top-6 z-50 w-64 -translate-x-1/2 rounded bg-gray-600 p-2 text-xs text-white shadow-lg pointer-events-none">
                        {t("_student_form._graduate_date_tooltip")}
                      </div>
                    )}
                  </div>
                </div>,
                <>
                  <div className="w-1/2">
                    <MonthPickerInput
                      value={graduateDate}
                      onChange={(date) => setGraduateDate(date)}
                    />
                  </div>
                </>,
              )}
              {renderInput(
                <div className="flex items-center space-x-1">
                  <span>{t("_student_form._reference_picture")}</span>
                  <span
                    className="relative inline-flex"
                    tabIndex={0}
                    onMouseEnter={(e) => {
                      updateReferenceTooltipPosition(e.currentTarget);
                      setShowReferenceTooltip(true);
                    }}
                    onMouseLeave={() => setShowReferenceTooltip(false)}
                    onFocus={(e) => {
                      updateReferenceTooltipPosition(e.currentTarget);
                      setShowReferenceTooltip(true);
                    }}
                    onBlur={() => setShowReferenceTooltip(false)}
                  >
                    <IoIosInformationCircle
                      size={21}
                      className="text-gray-600 cursor-pointer"
                    />
                    {showReferenceTooltip && (
                      <div
                        className="fixed z-[9999] w-72 rounded bg-gray-600 p-2 text-xs text-white whitespace-pre-line shadow-lg pointer-events-none -translate-x-1/2"
                        style={{
                          top: referenceTooltipPosition.y,
                          left: referenceTooltipPosition.x,
                        }}
                      >
                        {t("_student_form._reference_picture_tooltip")}
                      </div>
                    )}
                  </span>
                </div>,
                <TrainingPictureInput
                  uploadedPictures={trainingPictures}
                  isUpdate={isUpdate}
                  onChange={(files) => setTrainingPictures(files)}
                />,
              )}
              {isUpdate &&
                renderInput(
                  "",
                  <SavedTrainingPictures
                    uploadPictures={trainingPictures}
                    onUploadPicturesChange={(pictures) =>
                      setTrainingPictures(pictures)
                    }
                    updatedTrainingPictures={updatedTrainingPictures}
                    onUpdate={(pictures) =>
                      setUpdatedTrainingPictures(pictures)
                    }
                  />,
                )}
              {renderInput(
                `${t("_student_form._published_setting")}`,
                <div className="w-full">
                  <Checkbox
                    name={"not_publish"}
                    label={t("_student_form._do_not_publish")}
                    isChecked={isSetToNotPublish}
                    onChange={(status) => setIsSetToNotPublish(status)}
                  />
                </div>,
              )}
            </tbody>
          </table>
          <FormActionBar
            primaryLabel={t("_student_form._submit")}
            primaryDisabled={isDisabled}
            primaryLoading={loading}
          />
        </form>
      </Container>
      {actionLeftContent && (
        <div className="px-3 sm:px-5">{actionLeftContent}</div>
      )}
    </div>
  );
};

export default Form;
